import React from 'react';
import { Badge } from './badge';
import { cn } from '../../lib/utils';

/**
 * MobileTableCard - Reusable mobile card component for displaying table rows as cards
 *
 * @param {Object} props
 * @param {Object} props.item - The data item to display
 * @param {Array} props.fields - Array of field configurations
 * @param {string} props.primaryField - Main field to display prominently (e.g., "name")
 * @param {string} props.secondaryField - Secondary field below primary (e.g., "email")
 * @param {string} props.statusField - Field to show as status badge
 * @param {Array} props.badges - Additional badges to display
 * @param {ReactNode} props.actions - Action buttons to show at bottom
 * @param {Function} props.onClick - Click handler for the card
 * @param {string} props.className - Additional CSS classes
 */
const MobileTableCard = ({
  item,
  fields = [],
  primaryField,
  secondaryField,
  statusField,
  badges = [],
  actions,
  onClick,
  className,
  getStatusVariant,
}) => {
  return (
    <div
      className={cn(
        "border rounded-lg p-3 space-y-2 hover:bg-accent cursor-pointer transition-colors w-full max-w-full overflow-hidden",
        className
      )}
      onClick={onClick}
    >
      {/* Header: Primary Field + Status Badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className="font-medium text-sm line-clamp-2 break-words">
            {item[primaryField]}
          </span>
        </div>
        {statusField && item[statusField] && (
          <Badge
            variant={getStatusVariant ? getStatusVariant(item[statusField]) : 'default'}
            className="text-[10px] sm:text-xs flex-shrink-0 whitespace-nowrap"
          >
            {item[statusField]}
          </Badge>
        )}
      </div>

      {/* Secondary Info */}
      {secondaryField && item[secondaryField] && (
        <div className="text-[10px] sm:text-xs text-muted-foreground truncate">
          {item[secondaryField]}
        </div>
      )}

      {/* Divider if we have additional fields */}
      {fields.length > 0 && <div className="border-t" />}

      {/* Additional Fields */}
      {fields.filter(f => f.showOnMobile !== false).map((field) => {
        const value = item[field.key];
        if (!value && value !== 0) return null;

        return (
          <div key={field.key} className="flex justify-between items-center gap-2 text-[10px] sm:text-xs">
            <span className="text-muted-foreground flex-shrink-0">{field.label}:</span>
            <span className="font-medium text-right break-words min-w-0">
              {field.render ? field.render(value, item) : value}
            </span>
          </div>
        );
      })}

      {/* Badge Row */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {badges.map((badge, index) => (
            <Badge
              key={index}
              variant={badge.variant || 'outline'}
              className={cn('text-[10px]', badge.className)}
            >
              {badge.label || item[badge.key]}
            </Badge>
          ))}
        </div>
      )}

      {/* Actions */}
      {actions && (
        <div className="flex gap-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </div>
  );
};

export default MobileTableCard;
